import * as PrefabManager from '../PrefabManager';
import * as emitterManager from '../../utils/EmitterManager';
import * as AudioManager from '../../utils/AudioManager';
import { Settings } from '../../Settings';
import { Humanoid } from '../humanoids/Humanoid';
import { DroneBomb } from './DroneBomb';
import { bladeCut } from './bladeCut';

import {
    game
} from "../../Game";

const { getPointer } = Box2D;
const manifold = new Box2D.b2WorldManifold();
const vec = new Box2D.b2Vec2();

// The body's fixtures in the json: frame, saw, left rotor, right rotor.
const SAW = 1;
// Textures in a graphic group ignore their scale, so the saw art is shrunk here.
const SAW_SCALE = 0.55;
const SPIN = 25;
// How fast the saw's rim drags along whatever it touches, in m/s.
const RIM_SPEED = 8;
// Any touch cuts, and a part can be cut again sooner than with the katana (250 ms).
const SLICE_COOLDOWN = 50;
const GRIND_FRAMES = 3;
const GRIND_SOUND = 'skateboard_grind_loop';

export class SawDrone extends DroneBomb {
    constructor(target) {
        super(target);
        this.isExplosive = false;
        this.isSawDrone = true;
    }
    postConstructor() {
        super.postConstructor();
        const saw = this.lookupObject?.body?.myTexture?.children?.find(sprite => sprite.texture?.label?.startsWith('Saw'));
        saw?.scale.set(SAW_SCALE);
    }
    init() {
        super.init();
        const textures = this.body.myTexture.children;
        const named = name => textures.find(sprite => sprite.texture?.label?.startsWith(name));
        this.sawTexture = named('Saw');
        this.lightTexture = named('HommingDrone_Light');
        this.droneTexture = named('HommingBomb_SawDrone');

        // DroneBomb made every circle a rotor sensor; the saw is solid.
        this.saw = this.sides.find(fixture => fixture.verticeRef === SAW);
        this.sides = this.sides.filter(fixture => fixture !== this.saw);
        this.saw.SetSensor(false);
        const circle = Box2D.castObject(this.saw.GetShape(), Box2D.b2CircleShape);
        this.sawCenter = { x: circle.get_m_p().get_x(), y: circle.get_m_p().get_y() };
        this.sawRadius = circle.get_m_radius();

        this.spinning = this.prefabObject.settings.spinning;
        this.hits = [];
        this.grindFrames = 0;
        this.grindSpot = null;
        this.sawSparks = emitterManager.getLoopingEmitter('sparksMetal', this.body, this.body.GetPosition(), 0);
        this.sawSparks.emit = false;
        AudioManager.preloadSFX('sword-slash');
    }
    explode() {}
    // A character's torso, never another part or the vehicle they ride.
    isChaseTarget(body) {
        return body.isHumanoid && body.mySprite?.data?.refName === Humanoid.BODY_PARTS.BODY && super.isChaseTarget(body);
    }
    set(property, value) {
        if (property === 'active' || property === 'spinning') {
            this.prefabObject.settings.spinning = value;
            this.spinning = value;
            return;
        }
        super.set(property, value);
    }
    update() {
        super.update();
        if (this.destroyed) return;
        const deltaTime = game.editor.deltaTime;

        if (this.spinning && this.sawTexture) this.sawTexture.rotation += SPIN * deltaTime / 1000;

        const hits = this.hits;
        this.hits = [];
        for (const hit of hits) this.slice(hit);

        if (this.spinning && this.grindFrames > 0) {
            this.grindFrames--;
            const spot = this.grindSpot;
            // Sparks fly off along the rim, the way it turns.
            this.sawSparks.spawnPos.set(spot.x * Settings.PTM, spot.y * Settings.PTM);
            this.sawSparks.rotation = Math.atan2(spot.nx, -spot.ny) * game.editor.RAD2DEG;
            this.sawSparks.playOnce();
            AudioManager.playPrefabUniqueLoopSFX(this.prefabObject.key, GRIND_SOUND, 0.25, 1.4, this.body.GetPosition());
        } else {
            AudioManager.stopPrefabUniqueLoopSFX(this.prefabObject.key, GRIND_SOUND);
        }
        this.sawSparks.update(deltaTime / 1000);
    }
    slice(hit) {
        if (hit.body.destroyed) return;
        vec.Set(hit.x, hit.y);
        const point = this.body.GetLocalPoint(vec);
        const c = this.sawCenter;
        let ux = point.x - c.x;
        let uy = point.y - c.y;
        const length = Math.hypot(ux, uy) || 1;
        ux /= length;
        uy /= length;
        // The cut runs from the rim into the part; it reaches a saw's radius past the contact
        // so a spinning saw goes through instead of only grazing the surface.
        const r = this.sawRadius;
        hit.axis = { ux, uy, points: [{ x: point.x - ux * r, y: point.y - uy * r }, { x: point.x + ux * r, y: point.y + uy * r }] };
        hit.cooldown = SLICE_COOLDOWN;
        if (!bladeCut(this.body, hit.body, hit)) return;
        const part = hit.body.mySprite?.data?.refName;
        const damage = part === Humanoid.BODY_PARTS.HEAD || part === Humanoid.BODY_PARTS.BODY ? 1000 : 30;
        game.editor.retrieveSubClassFromBody(hit.body)?.dealDamage?.(damage);
    }
    destroy() {
        if (this.sawSparks) emitterManager.destroyEmitter(this.sawSparks);
        delete this.sawSparks;
        AudioManager.stopPrefabUniqueLoopSFX(this.prefabObject.key, GRIND_SOUND);
        super.destroy();
    }
    sawContact(contact) {
        if (contact.GetFixtureA() === this.saw) return contact.GetFixtureB();
        if (contact.GetFixtureB() === this.saw) return contact.GetFixtureA();
        return null;
    }
    initContactListener() {
        super.initContactListener();
        const self = this;
        const rotors = this.contactListener.BeginContact;
        this.contactListener.BeginContact = function (contact) {
            rotors(contact);
            const other = self.saw && self.sawContact(contact);
            if (!other || other.IsSensor() || !self.spinning || other.GetBody().isFlesh || self.inPortalOpening(self.saw, other)) return;
            AudioManager.playSFX('drone-wall', 0.3, 1.2 + 0.2 * Math.random(), self.body.GetPosition());
        };
        this.contactListener.PreSolve = function (contact) {
            // A contact keeps its tangent speed, so a stopped saw has to set it back to 0.
            if (self.saw && self.sawContact(contact)) contact.SetTangentSpeed(self.spinning ? -RIM_SPEED : 0);
        };
        this.contactListener.PostSolve = function (contact) {
            const other = self.saw && self.sawContact(contact);
            if (!other || !self.spinning) return;
            const count = contact.GetManifold().get_pointCount();
            if (!count) return;
            contact.GetWorldManifold(manifold);
            let x = 0;
            let y = 0;
            for (let j = 0; j < count; j++) {
                const point = manifold.get_points(j);
                x += point.get_x() / count;
                y += point.get_y() / count;
            }
            // The manifold's normal points from fixture A to fixture B; turn it to point away from the saw.
            const away = contact.GetFixtureA() === self.saw ? 1 : -1;
            const normal = manifold.get_normal();
            const nx = normal.get_x() * away;
            const ny = normal.get_y() * away;
            const body = other.GetBody();
            if (body.isFlesh) {
                if (!self.hits.some(hit => getPointer(hit.body) === getPointer(body))) self.hits.push({ body, x, y, nx, ny });
            } else {
                self.grindFrames = GRIND_FRAMES;
                self.grindSpot = { x, y, nx, ny };
            }
        };
    }
}

SawDrone.settings = Object.assign({}, DroneBomb.settings, {
    "chase": DroneBomb.chaseTypes.mainCharacter,
    "spinning": true,
});
SawDrone.settingsOptions = Object.assign({}, DroneBomb.settingsOptions, {
    "spinning": false,
});
for (const key of ['delay', 'force', 'active', 'activateOn']) {
    delete SawDrone.settings[key];
    delete SawDrone.settingsOptions[key];
}

PrefabManager.prefabLibrary.SawDrone = {
    json: JSON.stringify({ objects: [
        [0, 0.01, -0.051, 0, "sawdrone", "body", 0, ["#999999", "#999999", "#999999", "#999999"], ["#000", "#000", "#000", "#000"], [0, 0, 0, 0], false, true,
            [[{ x: -2.1, y: 0.16 }, { x: -2.1, y: -0.476 }, { x: 2.096, y: -0.476 }, { x: 2.096, y: 0.16 }],
                [{ x: -0.038, y: 1.2 }, { x: -0.038, y: 1.2 }],
                [{ x: -2.199, y: -0.33 }, { x: -2.199, y: -0.33 }],
                [{ x: 2.222, y: -0.33 }, { x: 2.222, y: -0.33 }]],
            [0.3, 0.06, 0.1, 0.1], 0, [0, 45, 6.651, 6.651], "", [1, 1, 1, 1], true, false, false, [0.5, 0.6, 0.5, 0.5], [0.2, 0.1, 0.2, 0.2]],
        [7, -0.292, 1.533, 0, "", "", 1, [
            JSON.stringify([1, -0.566, 39.025, 0, "", "", 3, "Saw0000", null, null, null, null, false, "#FFFFFF", 1, 1, 1, 0, 0, 0, true]),
            JSON.stringify([1, -0.432, -13.525, 0, "", "", 1, "HommingDrone_Light0000", null, null, null, null, false, "#FFFFFF", 1, 1, 1, 0, 0, 0, true]),
            JSON.stringify([1, 0.432, 11.025, 0, "sawdrone", "texture", 2, "HommingBomb_SawDrone0000", null, null, null, null, false, "#FFFFFF", 1, 1, 1, 0, 0, 0, true]),
        ], 0, 3.12, -1.759, 0, 1, 0, 0, 0, true],
    ] }),
    class: SawDrone,
    library: PrefabManager.LIBRARY_WEAPON,
}
